import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Shield, Plus } from 'lucide-react';
import { formatDate } from '../lib/utils';

export default function CompliancePage() {
  const { user, axiosAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const [records, setRecords] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: '', type: 'drug_test', date: '', status: 'pending', notes: '' });
  const api = axiosAuth();
  const canCreate = ['coordinator', 'admin', 'peer_specialist'].includes(user.role);

  const load = () => {
    api.get('/compliance').then(r => setRecords(r.data)).catch(() => {});
    if (canCreate) api.get('/users?role=client').then(r => setClients(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.client_id || !form.date) return toast.error('Client and date required');
    try {
      await api.post('/compliance', form);
      toast.success('Record added!');
      setOpen(false);
      setForm({ client_id: '', type: 'drug_test', date: '', status: 'pending', notes: '' });
      load();
    } catch { toast.error('Failed to add record'); }
  };

  const statusColors = { compliant: 'bg-emerald-50 text-emerald-700', non_compliant: 'bg-red-50 text-red-700', pending: 'bg-amber-50 text-amber-700' };
  const filteredRecords = useMemo(() => {
    if (!statusParam) return records;
    return records.filter(r => r.status === statusParam);
  }, [records, statusParam]);
  const hasFilter = Boolean(statusParam);

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Compliance Tracking</h1><p className="text-muted-foreground text-sm mt-1">Monitor attendance, drug tests, and program compliance</p></div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="new-compliance-button"><Plus size={16} /> Add Record</Button></DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Add Compliance Record</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="space-y-1.5"><Label>Client</Label>
                  <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}>
                    <SelectTrigger data-testid="compliance-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Type</Label>
                    <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="drug_test">Drug Test</SelectItem><SelectItem value="attendance">Attendance</SelectItem><SelectItem value="court_appearance">Court Appearance</SelectItem><SelectItem value="program_requirement">Program Requirement</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></div>
                </div>
                <div className="space-y-1.5"><Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="compliant">Compliant</SelectItem><SelectItem value="non_compliant">Non-Compliant</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes" /></div>
                <Button onClick={handleCreate} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="create-compliance-button">Add Record</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {hasFilter && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-700" data-testid="compliance-filter-summary">
          Showing: {statusParam} records
        </div>
      )}

      <div className="space-y-3">
        {filteredRecords.length > 0 ? filteredRecords.map(r => (
          <Card key={r.id} className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-slate-50 flex items-center justify-center shrink-0"><Shield className="w-5 h-5 text-slate-600" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm capitalize">{r.type?.replace('_', ' ')}</p>
                <p className="text-xs text-muted-foreground">{formatDate(r.date)}{r.notes ? ` &middot; ${r.notes}` : ''}</p>
              </div>
              <Badge className={statusColors[r.status] || 'bg-muted'} variant="secondary">{r.status?.replace('_', ' ')}</Badge>
            </CardContent>
          </Card>
        )) : (
          <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-8 text-center"><Shield className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No compliance records found.</p></CardContent></Card>
        )}
      </div>
    </div>
  );
}
